import logger from "quill/core/logger";
import { Root, ScrollBlot, EmbedBlot } from "parchment";
import { Placeholder } from "./placeholder";

const debug = logger("quill-placeholder-blot");

const BLOCK_CONTENT = /<(table|div|p|ul|ol|blockquote|h[1-6]|style)\b/i;

export const REMOVE_CLASS = "ql-placeholder-remove";

// Inherited text properties cross the shadow boundary, and the editor's white-space: pre-wrap
// would render every newline and indent of the pretty printed email HTML.
const SHADOW_RESET =
  ":host{white-space:normal;text-indent:0;letter-spacing:normal;word-spacing:normal;text-transform:none;line-height:normal;}";

export function readNodeData(node: any): any {
  if (!(node instanceof HTMLElement)) {
    return undefined;
  }
  const result: any = {};
  if (node.hasAttribute("data-id")) {
    result.id = node.getAttribute("data-id");
  }
  if (node.hasAttribute("data-label")) {
    result.label = node.getAttribute("data-label");
  }
  result.required = node.hasAttribute("data-required");
  return result;
}

export class PlaceholderEvent extends Event {
  public value: object;
  public event: Event;
  public sourceNode: HTMLElement;
  constructor(name: string, options: EventInit) {
    super(name, options);
    this.value = {};
    this.event = new Event(name);
  }
}

export class PlaceholderBlot extends EmbedBlot {
  static blotName = "placeholder";
  static tagName = "span";
  static className = "ql-placeholder-content";
  static delimiters: string[];
  // Resolved content per placeholder id. Empty when the host has nothing to resolve against
  // (batch editors, templates), which keeps the raw [[token]] rendering.
  static values: Record<string, string> = {};

  private mounted: boolean;
  private clickHandler?: EventListener;

  constructor(scroll: ScrollBlot, node: Node) {
    super(scroll, node);
    this.mounted = false;
    debug.log("PlaceholderBlot constructed", node);
  }

  static create(value: Placeholder) {
    debug.log("create static", value);
    const node = super.create();
    if (!(node instanceof HTMLElement)) {
      return node;
    }
    debug.log(node);
    if (value.required) {
      node.setAttribute("data-required", "true");
    }
    node.setAttribute("data-id", value.id);
    node.setAttribute("data-label", value.label);
    node.setAttribute("spellcheck", "false");

    // const contentNode = document.createElement('span');
    node.setAttribute("contenteditable", "false");
    const { delimiters } = PlaceholderBlot;
    const token =
      typeof delimiters === "string"
        ? `${delimiters}${value.id}${delimiters}`
        : `${delimiters[0]}${value.id}${delimiters[1] || delimiters[0]}`;
    node.setAttribute("title", token);
    const resolved = PlaceholderBlot.values[value.id];
    // The value carries the shadow root, so the remove icon can stay in the light DOM where the
    // editor styles reach it - a shadow host renders no children of its own.
    const body = document.createElement("span");
    body.className = "ql-placeholder-value";
    // An empty resolution (no signature, no other sales reps) would render a chip holding nothing,
    // so keep showing the name - it still tells the user what will be filled in on send.
    if (undefined === resolved || "" === resolved.trim()) {
      body.innerText = value.label;
    } else {
      node.classList.add("ql-placeholder-resolved");
      if (BLOCK_CONTENT.test(resolved)) {
        node.classList.add("ql-placeholder-block");
      }
      // The rendered quote summary carries its own <style>, which would restyle the whole app
      // from inside the editor. A shadow root keeps those rules - and ours - where they belong.
      const shadow = body.attachShadow({ mode: "open" });
      shadow.innerHTML = `<style>${SHADOW_RESET}</style>${resolved}`;
    }
    node.appendChild(body);

    if (!value.required) {
      const remove = document.createElement("span");
      remove.className = `${REMOVE_CLASS} p-chip-remove-icon`;
      remove.setAttribute("aria-hidden", "true");
      node.appendChild(remove);
    }

    return node;
  }

  static value(node: HTMLElement) {
    debug.log("value static");
    return readNodeData(node);
  }

  static formats(node: HTMLElement, scroll: Root): any {
    debug.log("formats (ignore)");
    return undefined;
  }

  format(name: string, value: any) {
    debug.log("format " + name + " (ignore)");
    //super.format(name, value);
  }

  attach() {
    debug.log("attach()");
    super.attach();
    if (!this.mounted) {
      // Register to allowedChildren / otherwise the node is removed during optimize phase. Bug?
      if (this.parent && this.parent.statics.allowedChildren) {
        const index =
          this.parent.statics.allowedChildren.indexOf(PlaceholderBlot);
        if (index < 0) {
          this.parent.statics.allowedChildren.push(PlaceholderBlot);
          debug.log("PlaceholderBlot alloweed for parent");
        }
      }
      // Add click handler
      this.clickHandler = this.getClickHandler();
      this.domNode.addEventListener("click", this.clickHandler, false);
    }
  }

  detach() {
    debug.log("detach()");
    super.detach();
    this.mounted = false;
    if (this.clickHandler) {
      this.domNode.removeEventListener("click", this.clickHandler);
      this.clickHandler = undefined;
    }
  }

  // Keyed by blot name so the delta insert resolves back to this blot on undo/redo; the static
  // value() stays bare because the clipboard wraps it itself.
  value() {
    debug.log("value");
    return { [PlaceholderBlot.blotName]: readNodeData(this.domNode) };
  }

  length(): number {
    return 1;
  }

  deleteAt(index: number, length: number): void {
    const placeholder = readNodeData(this.domNode);
    if (!placeholder || !placeholder.required) {
      super.deleteAt(index, length);
    }
  }

  getClickHandler(): EventListener {
    return (e) => {
      debug.log("onClick", e);
      const event = this.buildEvent("placeholder-clicked", e);
      this.scroll.domNode.dispatchEvent(event);
      e.preventDefault();
    };
  }

  private buildEvent(name: string, e: Event): PlaceholderEvent {
    const event = new PlaceholderEvent(name, {
      bubbles: true,
      cancelable: true,
    });
    event.value = Object.assign({}, (this.domNode as HTMLElement).dataset);
    event.event = e;
    event.sourceNode = this.domNode as HTMLElement;
    return event;
  }
}
